/** @jsx jsx */

/** @jsxFrag */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useState } from 'react';

import { useMutation } from '@apollo/client/react';

import type { InjectedRouter } from 'js/lib/connectToRouter';

import { Button, ButtonGroup, Dialog, Link, TextField, Typography2 } from '@coursera/cds-core';
import { email, required, runValidations } from '@coursera/coursera-ui/lib/utils/validation';
import type { UserEmail } from '@coursera/grpc-types-useremails/coursera/proto/useremails/v1/email_address';

import useShouldShowAlternativeEmailModal from 'bundles/program-home/queryHooks/useShowAlternateEmailModal';
import type {
  InsertUserEmailMutationResult,
  InsertUserEmailMutationVariables,
} from 'bundles/saml-sso/components/queries/InsertUserEmailMutation';
import { InsertUserEmailMutation } from 'bundles/saml-sso/components/queries/InsertUserEmailMutation';
import {
  SendVerificationEmailMutation,
  type SendVerificationEmailResult,
  type SendVerificationEmailVariables,
} from 'bundles/saml-sso/components/queries/SendVerificationEmailQuery';

import _t from 'i18n!nls/program-home';

const { HeadingGroup, Content, Actions } = Dialog;

type WrapperProps = {
  userId: number;
  programId: string;
  userEmails: UserEmail[];
  onClose?: () => void;
  router: InjectedRouter;
};

type Props = {
  userId: number;
  shouldShowAlternativeEmailModal: boolean;
  setModalState: (isOpen: boolean) => void;
  onUpdate: () => void;
  onClose?: () => void;
};

const styles = {
  input: css`
    margin-top: var(--cds-spacing-400);
    width: 100%;
  `,
  subtitle: css`
    margin-top: var(--cds-spacing-400);
  `,
  disclaimer: css`
    margin-top: var(--cds-spacing-200);
  `,
};

const STEPS = {
  ADD_EMAIL: 'ADD_EMAIL',
  CONFIRM_ADD: 'CONFIRM_ADD',
} as const;

export const ForcedSSORecoveryEmailModal = ({
  shouldShowAlternativeEmailModal,
  userId,
  setModalState,
  onClose,
  onUpdate,
}: Props) => {
  const [emailField, setEmailField] = useState<string>('');
  const [emailFieldError, setEmailFieldError] = useState<string>('');
  const [currentStep, setCurrentStep] = useState<keyof typeof STEPS>(STEPS.ADD_EMAIL);
  const [sendVerificationEmail] = useMutation<SendVerificationEmailResult, SendVerificationEmailVariables>(
    SendVerificationEmailMutation
  );

  const onEmailUpdate = () => {
    setCurrentStep(STEPS.CONFIRM_ADD);
    onUpdate?.();
  };

  const sendVerificationEmailUtil = (recoveryEmail: string) => {
    sendVerificationEmail({
      variables: {
        input: {
          emailAddress: recoveryEmail,
          userId,
        },
      },
    });
  };

  const [insertUserEmail, { loading }] = useMutation<InsertUserEmailMutationResult, InsertUserEmailMutationVariables>(
    InsertUserEmailMutation,
    {
      onError: ({ networkError }) => {
        // @ts-expect-error(apollo-v3): TS2345: Argument of type 'readonly GraphQLError[] | undefined' is not assignable
        if (networkError?.response?.status === 409) {
          setEmailFieldError(_t('This email is being used with another account! Try using a different email address'));
        } else {
          setEmailFieldError(_t('There was an error while adding the email, please try again'));
        }
      },
      onCompleted: (data) => {
        const addedEmail = data?.InsertUserEmailResponse?.userEmail?.emailAddress;
        if (addedEmail) {
          setEmailFieldError('');
          sendVerificationEmailUtil(addedEmail);
          onEmailUpdate();
        }
      },
    }
  );

  const onChangeEmail = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    setEmailField(value);
    setEmailFieldError('');
  };

  const getValidationErrors = () => {
    const errors = runValidations(emailField, [required, email]);
    return errors;
  };

  const onSubmitEmail = () => {
    const errors = getValidationErrors();

    if (errors) {
      setEmailFieldError(errors);
      return;
    }

    insertUserEmail({
      variables: {
        input: { emailAddress: emailField, isPrimary: false, userId },
      },
    });
  };

  const setAlternativeEmailLater = () => {
    setModalState(false);
    if (onClose) {
      onClose();
    }
  };

  const getModalContent = () => {
    if (currentStep === STEPS.ADD_EMAIL) {
      return (
        <>
          <HeadingGroup id="title">{_t('Protect your accomplishments')}</HeadingGroup>
          <Content>
            <Typography2 component="p" id="description">
              {_t('Add a recovery email address to secure your access and safeguard your accomplishments.')}
            </Typography2>
            <TextField
              css={styles.input}
              value={emailField || ''}
              onChange={onChangeEmail}
              onBlur={getValidationErrors}
              label={_t('Recovery email address')}
              name="recovery-email"
              data-testid="recovery-email-input"
              validationStatus={emailFieldError ? 'error' : undefined}
              validationLabel={emailFieldError}
            />
          </Content>
          <Actions>
            <ButtonGroup
              aria-label={_t('Submit or skip email')}
              primaryButtonProps={{
                children: _t('Add a recovery email'),
                onClick: onSubmitEmail,
                loading,
              }}
              alternateButtonProps={{
                children: _t('Remind me later'),
                variant: 'ghost',
                onClick: setAlternativeEmailLater,
              }}
            />
          </Actions>
        </>
      );
    }
    return (
      <>
        <HeadingGroup id="title">{_t('Check your email')}</HeadingGroup>
        <Content>
          <Typography2 component="p" id="description">
            {_t(
              'We emailed a magic link to #{emailAddress} to confirm your account. The link expires in 24 hours so be sure to do it soon!',
              {
                emailAddress: emailField,
              }
            )}
          </Typography2>
          <Typography2 css={styles.subtitle} component="p">
            {_t('Need to reenter your email? ')}
            <Link
              href="#"
              variant="standard"
              typographyVariant="bodyPrimary"
              onClick={() => setCurrentStep(STEPS.ADD_EMAIL)}
            >
              {_t('Update your email address')}
            </Link>
          </Typography2>

          <Typography2 css={styles.disclaimer} component="p">
            {_t('If you didn’t receive an email, you can')}
            <Link
              href="#"
              variant="standard"
              typographyVariant="bodyPrimary"
              onClick={() => setCurrentStep(STEPS.ADD_EMAIL)}
            >
              {_t(' try again.')}
            </Link>
          </Typography2>
        </Content>
        <Actions>
          <Button variant="primary" onClick={setAlternativeEmailLater} size="medium">
            {_t('Done')}
          </Button>
        </Actions>
      </>
    );
  };
  return (
    <Dialog
      aria-describedby="description"
      aria-labelledby="title"
      open={!!shouldShowAlternativeEmailModal}
      variant="standard"
      onClose={setAlternativeEmailLater}
    >
      {getModalContent()}
    </Dialog>
  );
};

export const ForcedSSORecoveryEmailModalWrapper = ({
  userId,
  userEmails,
  programId,
  router,
  onClose,
}: WrapperProps) => {
  const { shouldShowAlternativeEmailModal, setModalState } = useShouldShowAlternativeEmailModal(
    userId,
    userEmails,
    programId,
    router.location.query
  );
  const onRecoveryEmailClose = () => {
    router.push({
      pathname: router.location.pathname,
      params: router.params,
      query: { ...router.location.query, openRecoveryModal: 'false' },
    });
    onClose?.();
  };

  const onRecoveryEmailUpdate = () => {
    router.push({
      pathname: router.location.pathname,
      params: router.params,
      query: { ...router.location.query, refreshUserEmails: 'true' },
    });
  };

  return (
    <ForcedSSORecoveryEmailModal
      userId={userId}
      shouldShowAlternativeEmailModal={shouldShowAlternativeEmailModal}
      setModalState={setModalState}
      onClose={onRecoveryEmailClose}
      onUpdate={onRecoveryEmailUpdate}
    />
  );
};

export default ForcedSSORecoveryEmailModalWrapper;
